import { h } from '../lib/dom.js';
import { getState, fmtNum, filteredTransactions, CONFIG, explorerTxUrl } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import { t } from '../i18n/index.js';
import {
  setTxnFilter, setTxnAccountFilter, setTxnTimeFilter, setTxnSort, setTxnSearch, toggleExpand, exportCSV, exportPDF,
  setTagDraft, addTxnTag, removeTxnTag, openInNewWindow
} from '../actions.js';

function pill(label, active, onclick) {
  return h('div', { class: 'pill' + (active ? ' active' : ''), onclick }, label);
}

function tagChip(txn, tag, removable) {
  return h('span', { class: 'tag-chip' }, [
    h('span', {}, tag),
    removable ? h('button', {
      class: 'tag-chip-remove', 'aria-label': t('activity.removeTag', { tag }),
      onclick: (e) => { e.stopPropagation(); removeTxnTag(txn.id, tag); }
    }, h('span', { html: iconMarkup('x', { size: 10 }) })) : null
  ]);
}

// Editing only happens once a row is expanded — tapping the collapsed row's
// read-only chips (tagChip above, removable: false) still just toggles it
// open, same as tapping anywhere else on the row.
function tagEditor(s, txn) {
  const tags = s.txnTags[txn.id] || [];
  return h('div', { class: 'tag-editor', onclick: (e) => e.stopPropagation() }, [
    tags.length ? h('div', { class: 'tag-chip-row' }, tags.map((tag) => tagChip(txn, tag, true))) : null,
    h('div', { class: 'field-row' }, [
      h('input', {
        placeholder: t('activity.addTagPlaceholder'), value: s.tagDraft,
        'data-preserve-focus': 'tag-draft',
        oninput: (e) => setTagDraft(e.target.value),
        onkeydown: (e) => { if (e.key === 'Enter') addTxnTag(txn.id); }
      }),
      h('button', { class: 'btn btn-secondary btn-sm', onclick: () => addTxnTag(txn.id) }, t('activity.addTag'))
    ])
  ]);
}

export function renderActivity() {
  const s = getState();
  const txnFiltered = filteredTransactions(s);

  const accountSelect = h('select', { onchange: (e) => setTxnAccountFilter(e.target.value) }, [
    h('option', { value: 'all', selected: s.txnAccountFilter === 'all' || undefined }, t('activity.allAccounts')),
    ...s.wallets.map((a) => h('option', { value: a.id, selected: a.id === s.txnAccountFilter || undefined }, a.name))
  ]);

  const sortSelect = h('select', { onchange: (e) => setTxnSort(e.target.value) }, [
    h('option', { value: 'date-desc', selected: s.txnSort === 'date-desc' || undefined }, t('activity.sortNewest')),
    h('option', { value: 'date-asc', selected: s.txnSort === 'date-asc' || undefined }, t('activity.sortOldest')),
    h('option', { value: 'amount-desc', selected: s.txnSort === 'amount-desc' || undefined }, t('activity.sortAmountHighLow')),
    h('option', { value: 'amount-asc', selected: s.txnSort === 'amount-asc' || undefined }, t('activity.sortAmountLowHigh'))
  ]);

  // Desktop-dense-table header (style.css's data-table media query) — the
  // labels here must line up with each row's own tx-cell-date/desc/
  // account/amount below via the same grid-template-columns, hidden below
  // that breakpoint same as the cells themselves.
  const tableHeader = h('div', { class: 'tx-table-header' }, [
    h('span', { class: 'tx-cell-date' }, t('activity.colDate')),
    h('span', { class: 'tx-cell-desc' }, t('activity.colDescription')),
    h('span', { class: 'tx-cell-account' }, t('activity.colAccount')),
    h('span', { class: 'tx-cell-amount' }, t('activity.colAmount'))
  ]);

  const rows = txnFiltered.map((txn) => {
    // txn.amount is an exact signed decimal string (AUDIT.md F2) --
    // Number() here is display formatting, not storage.
    const amountFmt = (Number(txn.amount) < 0 ? '-' : '+') + fmtNum(Math.abs(Number(txn.amount))) + ' ' + txn.ccy;
    const expanded = s.expandedTxId === txn.id;
    const tags = s.txnTags[txn.id] || [];
    const desc = txn.type === 'out' ? t('activity.sentTo', { counterparty: txn.counterparty }) : t('activity.receivedFrom', { counterparty: txn.counterparty });
    // Two representations of the same date/account data, not a bug: on
    // mobile the dense-table cells (tx-cell-date/tx-cell-account) are
    // display:none and .tx-cell-date-mobile's combined "date · wallet"
    // line is what's shown (exactly the old markup); past the desktop
    // breakpoint that flips (style.css) so the grid's own date/account
    // columns show instead. Simplest way to get one real <table>-style
    // layout on wide screens without a second, divergent row renderer.
    const children = [
      h('div', { class: 'tx-row' }, [
        h('span', { class: 'tx-cell-date' }, txn.date),
        h('div', { class: 'tx-cell-desc' }, [
          h('div', { class: 'desc' }, desc),
          h('div', { class: 'meta flex-gap-sm items-center' }, [
            h('span', { class: 'tx-cell-date-mobile' }, `${txn.date} · ${txn.wallet}`),
            tags.length && !expanded ? h('div', { class: 'tag-chip-row' }, tags.map((tag) => tagChip(txn, tag, false))) : null
          ])
        ]),
        h('span', { class: 'tx-cell-account' }, txn.wallet),
        h('span', { class: 'tx-cell-amount amount ' + (Number(txn.amount) > 0 ? 'positive' : 'negative') }, amountFmt)
      ])
    ];
    if (expanded) {
      const explorerHref = explorerTxUrl(CONFIG.NET[txn.ccy] || '', txn.hash, s.testnetEnabled);
      children.push(h('div', { class: 'tx-detail' }, [
        h('div', { class: 'tx-detail-row' }, [
          h('span', {}, t('activity.hash', { hash: txn.hash })),
          explorerHref ? h('a', { href: explorerHref, target: '_blank', rel: 'noopener noreferrer' }, t('activity.viewOnChain')) : null
        ]),
        txn.type === 'fx' && txn.fxRate
          ? h('div', { class: 'tx-detail-row' }, h('span', {}, t('activity.fxRate', { ccy: txn.ccy, rate: txn.fxRate, currency: s.reportingCurrency })))
          : null
      ]));
      children.push(tagEditor(s, txn));
    }
    return h('div', { class: 'tx-row-wrap', onclick: () => toggleExpand(txn.id) }, children);
  });

  return h('div', { class: 'screen activity-screen' }, [
    h('div', { class: 'flex-between' }, [
      h('div', { class: 'flex-gap-sm items-center' }, [
        h('h1', { class: 'screen-title' }, t('activity.title')),
        h('button', {
          class: 'icon-btn-plain desktop-only-icon', 'aria-label': t('nav.openInNewWindow'), onclick: () => openInNewWindow('activity')
        }, h('span', { html: iconMarkup('squareArrowOutUpRight', { size: 15, color: '#757993' }) }))
      ]),
      h('div', { class: 'flex-gap-sm' }, [
        h('div', { class: 'icon-btn', onclick: exportPDF }, [h('span', { html: iconMarkup('filePdf', { size: 14 }) }), t('activity.pdf')]),
        h('div', { class: 'icon-btn', onclick: exportCSV }, [h('span', { html: iconMarkup('fileCsv', { size: 14 }) }), t('activity.csv')])
      ])
    ]),
    h('input', {
      placeholder: t('activity.searchPlaceholder'), value: s.txnSearch,
      'data-preserve-focus': 'txn-search',
      oninput: (e) => setTxnSearch(e.target.value)
    }),
    h('div', { class: 'flex-gap-sm select-row' }, [accountSelect, sortSelect]),
    h('div', { class: 'pill-row' }, [
      pill(t('activity.filterAll'), s.txnFilter === 'all', () => setTxnFilter('all')),
      pill(t('activity.filterIn'), s.txnFilter === 'in', () => setTxnFilter('in')),
      pill(t('activity.filterOut'), s.txnFilter === 'out', () => setTxnFilter('out')),
      pill(t('activity.filterAllTime'), s.txnTimeFilter === 'all', () => setTxnTimeFilter('all')),
      pill(t('activity.filter1M'), s.txnTimeFilter === '1m', () => setTxnTimeFilter('1m')),
      pill(t('activity.filter12M'), s.txnTimeFilter === '12m', () => setTxnTimeFilter('12m'))
    ]),
    rows.length
      ? h('div', { class: 'tx-list' }, [tableHeader, ...rows])
      : h('div', { class: 'empty-state' }, s.transactions.length ? t('activity.noMatches') : t('activity.noActivity'))
  ]);
}
